import { jsx as _jsx } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { resolveResponsiveClasses } from '#pplx/frontend/aether/primitives/BoxPrimitive/resolveResponsiveClasses.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import classNames from 'classnames';
import React, { useMemo } from 'react';
const FAMILY_CLASSES = {
    sans: 'font-sans',
    serif: 'font-serif',
    mono: 'font-mono',
    answer: 'font-answer',
};
const WEIGHT_CLASSES = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
};
const SIZE_CLASSES = {
    '2xs': 'text-2xs',
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
};
const LEADING_CLASSES = {
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
    '3': 'leading-3',
    '4': 'leading-4',
    '5': 'leading-5',
    '6': 'leading-6',
    '7': 'leading-7',
    '8': 'leading-8',
    '9': 'leading-9',
    '10': 'leading-10',
};
const TRACKING_CLASSES = {
    tighter: 'tracking-tighter',
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest',
};
// Variant class mappings
const VARIANT_CLASSES = {
    // Sans font variants
    micro: 'font-sans text-2xs font-normal',
    tiny: 'font-sans text-xs font-medium',
    tinyRegular: 'font-sans text-xs font-normal',
    tinyBold: 'font-sans text-xs font-bold',
    extraSmall: 'font-sans text-[13px]',
    small: 'font-sans text-sm',
    smallBold: 'font-sans text-sm font-medium',
    base: 'font-sans text-base',
    baseSemi: 'font-sans text-base font-medium',
    // Mono font variants
    tinyMono: 'font-mono text-2xs md:text-xs',
    smallMono: 'font-mono text-sm',
    smallCaps: 'text-2xs md:text-xs tracking-wide font-mono leading-none uppercase',
    // Display & Heading variants
    'section-title': 'font-sans text-lg font-bold',
    'page-title': 'font-sans font-bold text-2xl',
    display: 'font-serif text-3xl leading-[1.3] font-normal',
    // Entry title variants
    'entry-title': 'font-sans text-pretty text-xl lg:text-3xl font-medium',
    'entry-title-100': 'font-sans text-pretty text-xl lg:text-3xl font-medium',
    'entry-title-200': 'font-sans text-lg lg:text-xl font-medium',
    'entry-title-300': 'font-sans text-pretty font-medium',
    // Editorial
    editorial: 'font-editorial',
};
const TEXT_WRAP_CLASSES = {
    pretty: 'text-pretty',
    balance: 'text-balance',
    wrap: 'text-wrap',
    nowrap: 'text-nowrap',
};
const WHITESPACE_CLASSES = {
    normal: 'whitespace-normal',
    nowrap: 'whitespace-nowrap',
    pre: 'whitespace-pre',
    'pre-wrap': 'whitespace-pre-wrap',
    'pre-line': 'whitespace-pre-line',
    'break-spaces': 'whitespace-break-spaces',
};
// Only includes breakpoint/value combinations that are actually used.
// Add new entries here when using responsive whitespace with new breakpoint/value pairs.
const BREAKPOINT_WHITESPACE_CLASSES = {
    default: WHITESPACE_CLASSES,
    sm: {
        nowrap: 'sm:whitespace-nowrap',
    },
    md: {
        nowrap: 'md:whitespace-nowrap',
    },
};
const WORD_BREAK_CLASSES = {
    normal: 'break-normal',
    words: 'break-words',
    all: 'break-all',
};
const TEXT_TRANSFORM_CLASSES = {
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
    none: 'normal-case',
};
const SELECTION_CLASSES = {
    none: 'select-none',
    text: 'select-text',
    all: 'select-all',
    auto: 'select-auto',
};
const ALIGN_CLASSES = {
    start: 'text-start',
    center: 'text-center',
    end: 'text-end',
    justify: 'text-justify',
};
const LINE_CLAMP_CLASSES = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
    6: 'line-clamp-6',
    7: 'line-clamp-[7]',
    8: 'line-clamp-[8]',
    9: 'line-clamp-[9]',
    10: 'line-clamp-[10]',
    15: 'line-clamp-[15]',
    20: 'line-clamp-[20]',
};
// Only includes breakpoint/value combinations that are actually used.
// Add new entries here when using responsive lineClamp with new breakpoint/value pairs.
const BREAKPOINT_LINE_CLAMP_CLASSES = {
    default: LINE_CLAMP_CLASSES,
    sm: {
        5: 'sm:line-clamp-5',
    },
    md: {
        1: 'md:line-clamp-1',
        2: 'md:line-clamp-2',
        4: 'md:line-clamp-4',
    },
};
// Only includes breakpoint/value combinations that are actually used.
// Add new entries here when using responsive size with new breakpoint/value pairs.
const BREAKPOINT_SIZE_CLASSES = {
    default: SIZE_CLASSES,
    sm: {
        sm: 'sm:text-sm',
        base: 'sm:text-base',
        lg: 'sm:text-lg',
    },
    md: {
        base: 'md:text-base',
        lg: 'md:text-lg',
        xl: 'md:text-xl',
    },
    lg: {
        lg: 'lg:text-lg',
        xl: 'lg:text-xl',
        '2xl': 'lg:text-2xl',
    },
};
// Color class mappings
const COLOR_CLASSES = {
    default: 'text-primary',
    defaultInverted: 'text-inverse',
    light: 'text-secondary ',
    ultraLight: 'text-tertiary',
    caution: 'text-negative-primary',
    super: 'text-super-primary',
    white: 'text-white',
    positive: 'text-positive-primary',
    negative: 'text-negative-primary',
    warning: 'text-warning-primary',
    max: 'text-super-primary',
    'finance-positive': 'text-finance-positive-primary',
    'finance-negative': 'text-finance-negative-primary',
};
// Utility function to determine the HTML element
const getElementType = (variant, as) => {
    if (as)
        return as;
    if (variant === 'page-title')
        return 'h1';
    if (variant === 'entry-title')
        return 'h2';
    return 'div';
};
const SIZE_CLASS_SET = new Set(Object.values(SIZE_CLASSES));
const WEIGHT_CLASS_SET = new Set(Object.values(WEIGHT_CLASSES));
const FAMILY_CLASS_SET = new Set(Object.values(FAMILY_CLASSES));
/**
 * Strip classes from a variant string that would conflict with explicit props.
 * For example, if `size="2xl"` is passed, remove "text-base" from the variant
 * so both don't end up in the DOM.
 */
function stripOverriddenClasses(variantClass, overrides) {
    if (!overrides.size && !overrides.weight && !overrides.family)
        return variantClass;
    return variantClass
        .split(' ')
        .filter((cls) => {
        if (overrides.size && SIZE_CLASS_SET.has(cls))
            return false;
        if (overrides.weight && WEIGHT_CLASS_SET.has(cls))
            return false;
        if (overrides.family && FAMILY_CLASS_SET.has(cls))
            return false;
        return true;
    })
        .join(' ');
}
// Utility function to generate class names
const generateTextClasses = (args) => {
    const { variant, color, family, weight, size, leading, tracking, transform, selection, textWrap, whitespace, wordBreak, italic, tabularNums, truncate, lineClamp, textAlign, className, } = args;
    // When explicit props (size, weight, family) are provided, strip the
    // corresponding classes from the variant to avoid Tailwind conflicts
    // (e.g. variant "base" includes "text-base" which would conflict with size="2xl").
    const rawVariantClass = VARIANT_CLASSES[variant];
    const variantClass = stripOverriddenClasses(rawVariantClass, { size, weight, family });
    const colorClass = COLOR_CLASSES[color];
    const familyClass = family ? FAMILY_CLASSES[family] : '';
    const weightClass = weight ? WEIGHT_CLASSES[weight] : '';
    const sizeClass = resolveResponsiveClasses(size, SIZE_CLASSES, BREAKPOINT_SIZE_CLASSES);
    const leadingClass = leading ? LEADING_CLASSES[leading] : '';
    const trackingClass = tracking ? TRACKING_CLASSES[tracking] : '';
    const transformClass = transform ? TEXT_TRANSFORM_CLASSES[transform] : '';
    const userSelectClass = selection ? SELECTION_CLASSES[selection] : '';
    const textWrapClass = textWrap ? TEXT_WRAP_CLASSES[textWrap] : '';
    const whitespaceClass = resolveResponsiveClasses(whitespace, WHITESPACE_CLASSES, BREAKPOINT_WHITESPACE_CLASSES);
    const wordBreakClass = wordBreak ? WORD_BREAK_CLASSES[wordBreak] : '';
    const italicClass = italic ? 'italic' : '';
    const tabularNumsClass = tabularNums ? 'tabular-nums' : '';
    const truncateClass = truncate ? 'truncate' : '';
    const layoutClass = textAlign ? ALIGN_CLASSES[textAlign] : '';
    const selectionHighlightClass = 'selection:bg-super-selection selection:text-primary dark:selection:text-super-primary';
    const lineClampClass = resolveResponsiveClasses(lineClamp, LINE_CLAMP_CLASSES, BREAKPOINT_LINE_CLAMP_CLASSES);
    return classNames(className, variantClass, colorClass, familyClass, weightClass, sizeClass, leadingClass, trackingClass, transformClass, userSelectClass, textWrapClass, whitespaceClass, wordBreakClass, italicClass, tabularNumsClass, truncateClass, layoutClass, selectionHighlightClass, lineClampClass);
};
export const TextPrimitive = React.memo(function TextPrimitive({ children, variant = 'base', color = 'default', family, weight, size, leading, tracking, transform, selection, textWrap, whitespace, wordBreak, italic, tabularNums, truncate, textAlign, className, lineClamp, as, ref, padding, margin, rounded, display, direction, align, justify, gap, wrap, testId, ...rest }) {
    const el = useMemo(() => getElementType(variant, as), [variant, as]);
    const textClass = useMemo(() => generateTextClasses({
        variant,
        color,
        family,
        weight,
        size,
        leading,
        tracking,
        transform,
        selection,
        textWrap,
        whitespace,
        wordBreak,
        italic,
        tabularNums,
        truncate,
        lineClamp,
        textAlign,
        className,
    }), [
        className,
        color,
        family,
        weight,
        size,
        leading,
        tracking,
        transform,
        selection,
        textWrap,
        whitespace,
        wordBreak,
        italic,
        tabularNums,
        truncate,
        lineClamp,
        textAlign,
        variant,
    ]);
    if (typeof display === 'object') {
        return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, display: display, direction: direction, align: align, justify: justify, gap: gap, wrap: wrap, ...rest, children: children }));
    }
    switch (display) {
        case 'flex':
        case 'inline-flex':
            return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, display: display, direction: direction, align: align, justify: justify, gap: gap, wrap: wrap, ...rest, children: children }));
        case 'block':
        case 'inline-block':
            return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, display: display, ...rest, children: children }));
        case 'grid':
            return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, display: display, align: align, justify: justify, gap: gap, ...rest, children: children }));
        case 'none':
            return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, display: display, ...rest, children: children }));
        case undefined:
            return (_jsx(BoxPrimitive, { as: el, ref: ref, className: textClass, noBorder: true, padding: padding, margin: margin, rounded: rounded, testId: testId, ...rest, children: children }));
        default:
            assertUnreachableValue(display);
    }
});
//# sourceMappingURL=index.js.map